import { FC } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  SalaryAdvanceBulkToggleEmployeeStatusDocument,
  SalaryAdvanceEmployeeProfilesCollectionDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type Action = 'enable' | 'disable'

interface ModalConfiguration {
  title: string
  description: string
  confirmLabel: string
}

interface Props {
  open: boolean
  onClose: () => void
  employeeIds: string[]
  action: Action
}

const getModalContent = (action: Action, count: number): ModalConfiguration => {
  if (count === 1) {
    return action === 'enable'
      ? {
          title: i18n.t('salary_advance.employee.modal.enable.title'),
          description: i18n.t('salary_advance.employee.modal.enable.description'),
          confirmLabel: i18n.t('salary_advance.employee.modal.enable.button'),
        }
      : {
          title: i18n.t('salary_advance.employee.modal.disable.title'),
          description: i18n.t('salary_advance.employee.modal.disable.description'),
          confirmLabel: i18n.t('salary_advance.employee.modal.disable.button'),
        }
  }

  return action === 'enable'
    ? {
        title: i18n.t('salary_advance.employee.modal.enable.bulk_title', { count }),
        description: i18n.t('salary_advance.employee.modal.enable.bulk_description', { count }),
        confirmLabel: i18n.t('salary_advance.employee.modal.enable.button'),
      }
    : {
        title: i18n.t('salary_advance.employee.modal.disable.bulk_title', { count }),
        description: i18n.t('salary_advance.employee.modal.disable.bulk_description', { count }),
        confirmLabel: i18n.t('salary_advance.employee.modal.disable.button'),
      }
}

export const ToggleStatusModal: FC<Props> = ({ open, onClose, employeeIds, action }) => {
  const [toggleEmployeeStatus, { loading }] = useMutation(
    SalaryAdvanceBulkToggleEmployeeStatusDocument,
    {
      extractorFn: (payload) =>
        payload.salaryAdvanceMutations?.bulkToggleStatusSalaryadvanceEmployeeProfile ?? {
          errors: [],
          employeeProfiles: [],
        },
      refetchQueries: [SalaryAdvanceEmployeeProfilesCollectionDocument],
    }
  )

  const handleConfirm = async () => {
    const status = action === 'enable' ? 'enabled' : 'disabled'
    const employeeCount = employeeIds.length

    track(trackingEvents.salaryAdvance.employeesToggleStatusSubmit, {
      status_action: action,
      employee_count: employeeCount,
    })

    const result = await toggleEmployeeStatus({
      variables: {
        employeeIds,
        status,
      },
    })

    const handledResult = handleResult(result, () => {})

    track(trackingEvents.salaryAdvance.employeesToggleStatusResult, {
      status_action: action,
      employee_count: employeeCount,
      outcome: handledResult.success ? 'success' : 'failure',
    })

    if (handledResult.success) {
      const count = employeeCount
      app.setFlash({
        type: 'correct',
        message:
          count === 1
            ? action === 'enable'
              ? i18n.t('salary_advance.employee.toast.enabled_successfully')
              : i18n.t('salary_advance.employee.toast.disabled_successfully')
            : action === 'enable'
              ? i18n.t('salary_advance.employee.toast.bulk_enabled_successfully', { count })
              : i18n.t('salary_advance.employee.toast.bulk_disabled_successfully', { count }),
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  const { title, description, confirmLabel } = getModalContent(action, employeeIds.length)

  if (!open) return null

  return (
    <Modal
      illustration="alert"
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  )
}
